import React, { useState, useEffect, useRef } from 'react';
import { FaRegUserCircle, FaSearch, FaTimes, FaBars } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Profile from '../../components/profile/profile';
import axios from 'axios';
import API_BASE_URL from '../../components/apiconfig/api-config';

const Navbar = ({ sidebarWidth, sidebarOpen, setSidebarOpen, isMobile }) => {
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [userName, setUserName] = useState('User');
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [contractNo, setContractNo] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimeout = useRef(null);
  const navigate = useNavigate();

  // Fetch user profile data on component mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/profile/profile-get`,
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setUserName(response.data.data.name);
        setUserRole(response.data.data.role);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Keep default name if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = (value) => {
    if (!value) {
      setSuggestions([]);
      return;
    }
    fetch(`${API_BASE_URL}/api/report/master-report?purchase_contract_no=`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((contracts) => {
        if (!Array.isArray(contracts)) {
          setSuggestions([]);
          return;
        }
        const filtered = contracts.filter(
          (c) =>
            c.purchase_contract_no &&
            c.purchase_contract_no.toLowerCase().includes(value.toLowerCase())
        );
        setSuggestions(filtered);
      })
      .catch(() => setSuggestions([]));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setContractNo(value);
    setShowSuggestions(true);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 200);
  };

  const handleSuggestionClick = (contract) => {
    setContractNo(contract.purchase_contract_no);
    setShowSuggestions(false);
    setSuggestions([]);
    // Navigate to master report with the contract number
    navigate(`/report/master-report?contract=${encodeURIComponent(contract.purchase_contract_no)}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (contractNo.trim()) {
      setShowSuggestions(false);
      setSuggestions([]);
      // Navigate to master report with the contract number
      navigate(`/report/master-report?contract=${encodeURIComponent(contractNo.trim())}`);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const clearSearch = () => {
    setContractNo('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleProfileClick = () => {
    setShowProfilePanel(!showProfilePanel);
  };

  const handleClosePanel = () => {
    setShowProfilePanel(false);
    // Refresh user name after profile update
    fetchUserProfile();
  };

  const handleSidebarToggle = () => {
    if (sidebarOpen) {
      // If sidebar is open, close it completely
      setSidebarOpen(false);
    } else {
      // If sidebar is closed, open it
      setSidebarOpen(true);
    }
  };

  return (
    <>
      <nav
        className="fixed top-0 z-40 flex items-center justify-between px-2 sm:px-4 h-[57px] bg-white border-b border-gray-200 transition-all duration-200 no-print"
        style={{ 
          marginLeft: isMobile ? 0 : sidebarWidth, 
          width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)` 
        }}
      >
        {/* Left side - Mobile menu button and Search */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Sidebar toggle button - show when sidebar is hidden */}
          <button
            onClick={handleSidebarToggle}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            title="Open Sidebar"
          >
            <FaBars className="text-gray-600 text-lg" />
          </button>
          
          {/* Search Bar */}
          <div className="relative w-48 sm:w-64 md:w-80">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search contract..."
                  value={contractNo}
                  onChange={handleInputChange}
                  onFocus={() => contractNo && setShowSuggestions(true)}
                  onBlur={handleBlur}
                  className="w-full border border-gray-300 px-3 py-2 pl-9 pr-8 focus:outline-none focus:border-[#10a5e9] text-gray-700 placeholder-gray-500 bg-white rounded text-sm"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                {contractNo && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                )}
              </div>
            </form>
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {suggestions.map((c, idx) => (
                  <div
                    key={c.purchase_contract_no}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-gray-700 text-sm border-b border-gray-100 last:border-b-0"
                    onMouseDown={() => handleSuggestionClick(c)}
                  >
                    {c.purchase_contract_no}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right side - User info and profile */}
        <div className="flex items-center gap-2 sm:gap-3 relative">
          <div className="hidden sm:flex flex-col items-end">
            <span className="text-sm font-semibold text-gray-800">
              {loading ? 'Loading...' : userName}
            </span>
            {!loading && userRole && (
              <span className="text-xs text-gray-500 capitalize">
                {userRole}
              </span>
            )}
          </div>
          <button 
            onClick={handleProfileClick}
            className={`rounded-full p-1.5 flex items-center justify-center shadow-inner transition-all ${
              showProfilePanel 
                ? 'bg-sky-200 shadow-md' 
                : 'bg-sky-100 hover:bg-sky-200 hover:shadow'
            }`}
            title="View Profile"
          >
            <FaRegUserCircle className="text-xl text-sky-600" />
          </button>

          {/* Profile Panel */}
          {showProfilePanel && (
            <div className="absolute top-full right-0 mt-1 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
              <div className="p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-gray-900">Profile</h3>
                  <button
                    onClick={handleClosePanel}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <FaTimes className="text-sm" />
                  </button>
                </div>
                <Profile onClose={handleClosePanel} />
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Backdrop for profile panel */}
      {showProfilePanel && (
        <div 
          className="fixed inset-0 z-30"
          onClick={handleClosePanel}
        ></div>
      )}
    </>
  );
};

export default Navbar;